import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LeadNav } from './LeadNav';
import { LeadHeader } from './LeadHeader';
import { LeadSection } from './LeadSection';
import '../styles/Lead.css';
import { useOptimizedResize } from '../utils/performance';
import { useNavVariant } from '../App';

export const Lead = ({ user_id }) => {
    const { id } = useParams();
    const navVariant = useNavVariant();
    const [leadData, setLeadData] = useState({});
    const [sections, setSections] = useState([]);
    const [activeSection, setActiveSection] = useState(1);
    const [folders, setFolders] = useState({});
    const [customFields, setCustomFields] = useState([]);
    const [relatedCases, setRelatedCases] = useState([]);
    const [metaData, setMetaData] = useState([]);
    const [fieldValues, setFieldValues] = useState([]);
    const [phases, setPhases] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useOptimizedResize(() => {
        setScreenWidth(window.innerWidth);
    });

    const fetchLead = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/leads.php?id=${id}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setLeadData(data.lead || {});
            } else {
                console.error("Error fetching lead:", data.message);
            }
        } catch (error) {
            console.error("Error fetching lead:", error);
        }
    };

    const fetchCustomFields = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php?lead_id=${id}&case_type_id=${leadData.case_type_id}&section_id=2`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setCustomFields(data.custom_fields.filter(f => (Number(f.case_type_id) === leadData.case_type_id && !JSON.parse(f.section_id).includes(47)) || f.required === 1) || []);
                setFieldValues(data.field_values || data.field_updates || []);
            } else {
                console.error("Error fetching custom fields:", data.message);
            }
        } catch (error) {
            console.error("Error fetching custom fields:", error);
        }
    };

    const fetchContacts = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            setContacts(data.contacts);
        } catch (error) {
            console.error("Error fetching contacts:", error);
        }
    };

    const fetchPhases = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/sections.php?template_id=1&lead_id=${id}`);
            const data = await response.json();
            if (data.success) {
                setPhases(data.phases || []);
            }
            else {
                console.error("Error fetching phases:", data.message);
            }
        }
        catch (error) {
            console.error("Error fetching phases:", error);
        }
    };

    const fetchRelatedCases = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/cases.php?lead_id=${id}&related=true`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setRelatedCases(data.cases || []);
            } else {
                console.error("Error fetching related cases:", data.message);
            }
        } catch (error) {
            console.error("Error fetching related cases:", error);
        }
    };

    const fetchDocuments = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/lead_documents.php?lead_id=${id}&${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                const folderStructure = data.folders || {};
                setFolders(folderStructure);
                setMetaData(data.documents || []);
            } else {
                console.error("Error fetching documents:", data.message);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
        }
    };

    useEffect(() => {
        const updatedSections = [
            { id: 1, name: "Incident", icon: "Clipboard" },
            { id: 2, name: "Intake", icon: "FilePlus2" },
            { id: 3, name: "Related Cases", icon: "Briefcase" },
            { id: 4, name: "Contact", icon: "Users" },
            { id: 5, name: "Activity Feed", icon: "Message Square" },
            /*{ id: 6, name: "Tasks", icon: "Square Check" }*/
        ];

        if (leadData?.pfs === 1) {
            updatedSections.push({ id: 7, name: "Uber PFS", icon: "File Stack" });
        }

        updatedSections.push({ id: 8, name: "Documents", icon: "File" });

        setSections(updatedSections);
    }, [leadData.pfs]);

    useEffect(() => {
        fetchLead();
        fetchContacts();
    }, [id]);

    useEffect(() => {
        if (leadData.id) {
            fetchCustomFields();
            fetchRelatedCases();
            fetchDocuments();
            fetchPhases();
        }
    }, [leadData]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sectionParam = params.get("section");

        document.title = `${leadData?.contact?.full_name ? leadData.contact.full_name : 'Lead'} | Lead`;
        
        if (sectionParam) {
            const sectionFound = sections.find(s => s.name.toLowerCase() === sectionParam.toLowerCase());
            if (sectionFound) {
                setActiveSection(sectionFound.id);
            }
        }
    }, []);

    return (
        <div className={`case-container lead-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            {screenWidth >= 768 && <LeadNav
                sections={sections} 
                activeSection={activeSection} 
                setActiveSection={setActiveSection} 
            />}
            <div className='lead-body'>
                <LeadHeader 
                    leadData={leadData} 
                    fetchLead={fetchLead} 
                    small={screenWidth < 768}
                    phases={phases}
                />
                {screenWidth < 768 && (
                    <LeadNav 
                        sections={sections} 
                        activeSection={activeSection} 
                        setActiveSection={setActiveSection}
                        small={true}
                    />
                )}
                <LeadSection 
                    user_id={user_id}
                    lead_id={leadData.id}
                    leadData={leadData}
                    section_id={activeSection}
                    customFields={customFields}
                    relatedCases={relatedCases}
                    folders={folders}
                    metaData={metaData}
                    fetchDocuments={fetchDocuments}
                    phases={phases}
                    fetchCustomFields={fetchCustomFields}
                    fieldValues={fieldValues}
                    fetchLead={fetchLead}
                    contacts={contacts}
                    fetchContacts={fetchContacts}
                />
            </div>
        </div>
    );
};
